import { skipToken, useInfiniteQuery } from '@tanstack/react-query';

import { Agents } from '@air/api';
import { callAirApi } from '@air/utils-api';

import { CLIPS_LIST_BY_IDS_MAX_IDS } from '~/components/v2/components/workspace/workspace-agent-panel/utils/chunkWorkspaceAgentClipIds';
import { isWorkspaceAgentSessionUnresolvableError } from '~/components/v2/components/workspace/workspace-agent-panel/utils/isWorkspaceAgentSessionUnresolvableError';

export const PRIVATE_WORKSPACE_AGENT_SESSION_ARTIFACT_ROWS_INFINITE_KEY =
  'PRIVATE_WORKSPACE_AGENT_SESSION_ARTIFACT_ROWS_INFINITE';
/** Sized to `Clips.listByIds`'s id cap so each row page resolves with one clips lookup. */
const ARTIFACT_ROW_PAGE_LIMIT = CLIPS_LIST_BY_IDS_MAX_IDS;
const ARTIFACT_ROW_TRANSIENT_RETRIES = 1;

export type PrivateWorkspaceAgentSessionArtifactRowsInfiniteParams = Omit<
  NonNullable<Parameters<typeof Agents.Sessions.Artifacts.Rows.list>[0]>,
  'agentType' | 'artifactId' | 'cursor' | 'limit' | 'sessionId' | 'workspaceId'
> & {
  artifactId: string | undefined;
  sessionId: string | undefined;
  workspaceId: string | undefined;
};

export type PrivateWorkspaceAgentSessionArtifactRowsInfiniteKey = [
  typeof PRIVATE_WORKSPACE_AGENT_SESSION_ARTIFACT_ROWS_INFINITE_KEY,
  PrivateWorkspaceAgentSessionArtifactRowsInfiniteParams,
];

export const getPrivateWorkspaceAgentSessionArtifactRowsInfiniteKey = (
  params: PrivateWorkspaceAgentSessionArtifactRowsInfiniteParams,
): PrivateWorkspaceAgentSessionArtifactRowsInfiniteKey => [
  PRIVATE_WORKSPACE_AGENT_SESSION_ARTIFACT_ROWS_INFINITE_KEY,
  params,
];

export const usePrivateWorkspaceAgentSessionArtifactRowsInfinite = (
  params: PrivateWorkspaceAgentSessionArtifactRowsInfiniteParams,
) => {
  const { artifactId, sessionId, workspaceId } = params;

  const queryFn =
    !workspaceId || !sessionId || !artifactId
      ? skipToken
      : ({ pageParam }: { pageParam: string }) =>
          callAirApi(() =>
            Agents.Sessions.Artifacts.Rows.list({
              agentType: 'workspace',
              ...params,
              artifactId,
              cursor: pageParam || undefined,
              limit: ARTIFACT_ROW_PAGE_LIMIT,
              sessionId,
              workspaceId,
            }),
          );

  return useInfiniteQuery({
    queryKey: getPrivateWorkspaceAgentSessionArtifactRowsInfiniteKey(params),
    queryFn,
    initialPageParam: '',
    getNextPageParam: (lastPage) => (lastPage.pagination?.hasMore ? (lastPage.pagination.cursor ?? null) : null),
    enabled: !!workspaceId && !!sessionId && !!artifactId,
    retry: (failureCount, error) =>
      !isWorkspaceAgentSessionUnresolvableError(error) && failureCount < ARTIFACT_ROW_TRANSIENT_RETRIES,
  });
};
